import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { JsonView } from '@/components/JsonView';
import { Alerts } from './Alerts';
import { OutcomeList, type ImportOutcome } from '@/components/Portability';
import { PageHeader } from '@/components/Layout';
import { Badge, Button, Card, Dialog, ErrorNote, Input, Loading, Select, Tabs } from '@/components/ui';
import { api } from '@/lib/api';
import { fmtDateTime, fmtWhen } from '@/lib/format';
import { rk, useMe, useProjects, useTools } from '@/lib/hooks';

type Tab = 'members' | 'keys' | 'alerts' | 'tools' | 'import' | 'audit';

export const Settings = () => {
  const [tab, setTab] = useState<Tab>('members');
  return (
    <>
      <PageHeader title="Settings" />
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: 'members', label: 'Members' },
          { value: 'keys', label: 'API keys' },
          { value: 'alerts', label: 'Alerts' },
          { value: 'tools', label: 'Registered tools' },
          { value: 'import', label: 'Import / export' },
          { value: 'audit', label: 'Audit log' },
        ]}
      />
      <div className="mt-5">
        {tab === 'members' && <Members />}
        {tab === 'keys' && <Keys />}
        {tab === 'alerts' && <Alerts />}
        {tab === 'tools' && <Tools />}
        {tab === 'import' && <ImportExport />}
        {tab === 'audit' && <Audit />}
      </div>
    </>
  );
};

const Members = () => {
  const me = useMe();
  const admin = me.data?.role === 'admin';
  const qc = useQueryClient();
  const q = useQuery({ queryKey: rk('members'), queryFn: () => api<{ members: Array<{ id: string; email: string; role: string; createdAt: string }> }>('/v1/members') });
  const [form, setForm] = useState({ email: '', role: 'editor' });
  const add = useMutation({
    mutationFn: (body: { email: string; role: string }) => api('/v1/members', { body }),
    onSuccess: () => {
      setForm({ email: '', role: 'editor' });
      void qc.invalidateQueries({ queryKey: rk('members') });
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/v1/members/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: rk('members') }),
    onError: (e) => toast.error((e as Error).message),
  });
  return (
    <Card title="Who can sign in" actions={<span className="text-xs text-ink-3">Members sign in with their existing account; add them here to grant access.</span>}>
      {admin && (
        <form
          className="flex flex-wrap gap-2 border-b border-line p-3"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate(form);
          }}
        >
          <Input className="w-72" type="email" placeholder="name@company.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          <Select className="w-32" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option value="viewer">viewer</option>
            <option value="editor">editor</option>
            <option value="admin">admin</option>
          </Select>
          <Button type="submit" variant="primary" loading={add.isPending}>
            Add
          </Button>
        </form>
      )}
      {q.isLoading ? (
        <Loading />
      ) : (
        <ul>
          {q.data?.members.map((m) => (
            <li key={m.id} className="flex items-center gap-3 border-b border-line-2 px-4 py-2 last:border-0">
              <span className="flex-1">{m.email}</span>
              {admin && m.email !== me.data?.email ? (
                <Select className="h-7 w-28" value={m.role} onChange={(e) => add.mutate({ email: m.email, role: e.target.value })}>
                  <option value="viewer">viewer</option>
                  <option value="editor">editor</option>
                  <option value="admin">admin</option>
                </Select>
              ) : (
                <Badge>{m.role}</Badge>
              )}
              {admin && m.email !== me.data?.email && (
                <button className="rounded p-1 text-ink-3 hover:text-bad" onClick={() => confirm(`Remove ${m.email}?`) && remove.mutate(m.id)}>
                  <Trash2 className="size-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="border-t border-line px-4 py-2 text-xs text-ink-3">Viewers see agents and requests. Editors change prompts, publish and use the playground. Admins also manage models, members and keys.</p>
    </Card>
  );
};

const Keys = () => {
  const me = useMe();
  const qc = useQueryClient();
  const projects = useProjects();
  const admin = me.data?.role === 'admin';
  const q = useQuery({
    queryKey: rk('keys'),
    queryFn: () => api<{ keys: Array<{ id: string; name: string; prefix: string; project: string; createdBy: string; createdAt: string; lastUsedAt: string | null; revokedAt: string | null; canPublishLessons?: boolean }> }>('/v1/api-keys'),
    enabled: admin,
  });
  const [form, setForm] = useState({ name: '', project: '' });
  const [minted, setMinted] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => api<{ key: string }>('/v1/api-keys', { body: { name: form.name, project: form.project || projects.data?.projects[0]?.slug } }),
    onSuccess: (res) => {
      setMinted(res.key);
      setForm({ name: '', project: '' });
      void qc.invalidateQueries({ queryKey: rk('keys') });
    },
  });
  const allowPublish = useMutation({
    mutationFn: (v: { id: string; on: boolean }) => api(`/v1/api-keys/${v.id}`, { method: 'PATCH', body: { canPublishLessons: v.on } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: rk('keys') }),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/v1/api-keys/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: rk('keys') }),
  });
  if (!admin) return <p className="text-[13px] text-ink-3">Only admins manage API keys.</p>;
  return (
    <Card title="Project API keys" actions={<span className="text-xs text-ink-3">For hosts outside this AWS account (HTTPS transport). In-account Lambdas use S3 + SQS and need no key.</span>}>
      <form
        className="flex flex-wrap gap-2 border-b border-line p-3"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <Input className="w-64" placeholder="Key name (e.g. local dev)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <Select className="w-40" value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })}>
          {(projects.data?.projects ?? []).map((p) => (
            <option key={p.id} value={p.slug}>
              {p.name}
            </option>
          ))}
        </Select>
        <Button type="submit" variant="primary" loading={create.isPending}>
          Create key
        </Button>
        {create.error ? <ErrorNote error={create.error} /> : null}
      </form>
      <ul>
        {q.data?.keys.map((k) => (
          <li key={k.id} className="flex flex-wrap items-center gap-3 border-b border-line-2 px-4 py-2 last:border-0">
            <span className="mono">{k.prefix}…</span>
            <span className="flex-1">{k.name}</span>
            <Badge>{k.project}</Badge>
            <span className="text-xs text-ink-3">used {fmtWhen(k.lastUsedAt)}</span>
            {!k.revokedAt && (
              <label
                className="flex items-center gap-1.5 text-xs text-ink-2"
                title="The key's learning job may publish the lessons drafts and examples snippets it writes itself (only the lessons block of a prompt; never a person's draft). Off: a person publishes them."
              >
                <input
                  type="checkbox"
                  checked={Boolean(k.canPublishLessons)}
                  disabled={allowPublish.isPending}
                  onChange={(e) =>
                    (!e.target.checked || confirm('Let this key publish its lessons drafts and examples by itself? Live prompts will change without a person publishing them.')) &&
                    allowPublish.mutate({ id: k.id, on: e.target.checked })
                  }
                />
                Publishes its lessons
              </label>
            )}
            {k.revokedAt ? (
              <Badge tone="bad">revoked</Badge>
            ) : (
              <Button size="sm" variant="danger" onClick={() => confirm('Revoke this key? Hosts using it stop immediately.') && revoke.mutate(k.id)}>
                Revoke
              </Button>
            )}
          </li>
        ))}
      </ul>
      <Dialog open={Boolean(minted)} onClose={() => setMinted(null)} title="Copy your key now" footer={<Button onClick={() => setMinted(null)}>Done</Button>}>
        <p className="mb-2 text-[13px] text-ink-2">It won't be shown again. Store it as a secret on the host.</p>
        <div className="flex gap-2">
          <Input readOnly className="mono" value={minted ?? ''} />
          <Button onClick={() => void navigator.clipboard.writeText(minted ?? '').then(() => toast.success('Copied'))}>
            <Copy className="size-3.5" />
          </Button>
        </div>
      </Dialog>
    </Card>
  );
};

const Tools = () => {
  const q = useTools();
  if (q.isLoading) return <Loading />;
  if (!q.data?.manifests.length) return <p className="text-[13px] text-ink-3">No host has registered tools yet. They appear after product code calls registerTools and runs an agent once.</p>;
  return (
    <div className="flex flex-col gap-4">
      {q.data.manifests.map((m) => (
        <Card key={m.project} title={m.project} actions={<span className="text-xs text-ink-3">updated {fmtWhen(m.updatedAt)}</span>}>
          <ul>
            {m.tools.map((t) => (
              <li key={t.name} className="border-b border-line-2 px-4 py-2 last:border-0">
                <div className="mono font-medium">{t.name}</div>
                <div className="text-xs text-ink-2">{t.description}</div>
                <details className="mt-1">
                  <summary className="cursor-pointer text-xs text-ink-3">parameters</summary>
                  <JsonView value={t.parameters} className="mt-1" />
                </details>
              </li>
            ))}
          </ul>
        </Card>
      ))}
    </div>
  );
};

const Audit = () => {
  const q = useQuery({
    queryKey: rk('audit'),
    queryFn: () => api<{ entries: Array<{ id: string; actor: string; action: string; entityType: string; entityId: string | null; at: string; after: unknown }> }>('/v1/audit?limit=200'),
  });
  if (q.isLoading) return <Loading />;
  return (
    <Card>
      <table className="w-full text-[13px]">
        <tbody>
          {q.data?.entries.map((e) => (
            <tr key={e.id} className="border-b border-line-2 last:border-0">
              <td className="px-4 py-2 whitespace-nowrap text-ink-3">{fmtDateTime(e.at)}</td>
              <td className="px-3 py-2">{e.actor}</td>
              <td className="px-3 py-2">
                <Badge tone="brand">{e.action}</Badge>
              </td>
              <td className="mono max-w-[480px] truncate px-4 py-2 text-xs text-ink-2">{e.after ? JSON.stringify(e.after) : e.entityId}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
};

const ImportExport = () => {
  const me = useMe();
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<ImportOutcome[] | null>(null);
  const parsed = (() => {
    try {
      return text.trim() ? (JSON.parse(text) as unknown) : null;
    } catch {
      return undefined;
    }
  })();
  const dry = useMutation({
    mutationFn: () => api<{ outcomes: ImportOutcome[] }>('/v1/import', { body: { bundle: parsed, dryRun: true } }),
    onSuccess: (r) => setPreview(r.outcomes),
  });
  const run = useMutation({
    mutationFn: () => api<{ outcomes: ImportOutcome[] }>('/v1/import', { body: { bundle: parsed } }),
    onSuccess: (r) => {
      setPreview(r.outcomes);
      toast.success('Imported — new and changed agents are drafts');
      void qc.invalidateQueries({ queryKey: rk('agents') });
      void qc.invalidateQueries({ queryKey: rk('snippets') });
    },
  });
  const exportAll = useMutation({
    mutationFn: () => api('/v1/export', { body: {} }),
    onSuccess: (bundle) => {
      const url = URL.createObjectURL(new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' }));
      Object.assign(document.createElement('a'), { href: url, download: `agents-${new Date().toISOString().slice(0, 10)}.json` }).click();
      URL.revokeObjectURL(url);
    },
  });
  const editor = me.data?.role === 'admin' || me.data?.role === 'editor';
  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
      <Card title="Import a bundle">
        <div className="flex flex-col gap-3 p-4">
          <p className="text-[13px] text-ink-2">
            Paste or load an export from another environment or region (or <span className="mono">npm run agents:export</span> from product code). New agents arrive as drafts, changed ones as a new draft version; nothing live changes until someone publishes.
          </p>
          <input
            type="file"
            accept="application/json,.json"
            className="text-[13px]"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (f) {
                setText(await f.text());
                setPreview(null);
              }
            }}
          />
          <textarea className="mono h-48 rounded-md border border-line p-2 text-xs" value={text} onChange={(e) => (setText(e.target.value), setPreview(null))} placeholder='{"schema":"agent-manager/import@1","agents":[…]}' />
          {parsed === undefined && <p className="text-xs text-bad">Not valid JSON</p>}
          <div className="flex gap-2">
            <Button disabled={!parsed || !editor} loading={dry.isPending} onClick={() => dry.mutate()}>
              Preview
            </Button>
            <Button variant="primary" disabled={!parsed || !editor || !preview || preview.some((o) => o.result === 'error')} loading={run.isPending} onClick={() => run.mutate()}>
              Import
            </Button>
          </div>
          {(dry.error || run.error) && <ErrorNote error={dry.error ?? run.error} />}
          {preview && <OutcomeList outcomes={preview} />}
        </div>
      </Card>
      <Card title="Export">
        <div className="flex flex-col gap-3 p-4">
          <p className="text-[13px] text-ink-2">Download every live agent and flow (with the snippets they include) as one bundle — a backup, or to seed another environment.</p>
          <Button loading={exportAll.isPending} onClick={() => exportAll.mutate()}>
            Export all live agents
          </Button>
          <p className="text-xs text-ink-3">To copy one agent to another region, use “Copy to …” on its page.</p>
        </div>
      </Card>
    </div>
  );
};
